import { PermissionTypes } from '@app/constants/enums/permission';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { capitalize, getAccessCode, getRoutePath } from '@app/utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { sidebarNavigation, SidebarNavigationItem } from '../sidebarNavigation';
import * as S from './SiderMenu.styles';
import { Badge, Typography } from 'antd';

interface SiderContentProps {
  unreadMessage: number;
  unreadDealRoom: number;
  setCollapsed: (isCollapsed: boolean) => void;
}

const SiderMenu: React.FC<SiderContentProps> = ({ unreadMessage, unreadDealRoom, setCollapsed }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [sideNavigation, setSideNavigation] = useState<SidebarNavigationItem[]>([]);

  const sidebarNavFlat = sidebarNavigation.reduce(
    (result: SidebarNavigationItem[], current) =>
      result.concat(current.children && current.children.length > 0 ? current.children : current),
    [],
  );

  const pathName = getRoutePath(location, params);
  const currentMenuItem = sidebarNavFlat.find(
    ({ url, subUrls }) => url === location.pathname || (subUrls && subUrls.includes(pathName)),
  );
  const defaultSelectedKeys = currentMenuItem ? [currentMenuItem.key] : [];
  const openedSubmenu = sidebarNavigation.find(({ children }) =>
    children?.some(({ url }) => url === location.pathname),
  );
  const defaultOpenKeys = openedSubmenu ? [openedSubmenu.key] : [];
  const user = useAppSelector((state) => state?.user?.user);
  const userPermission = useAppSelector((state) => state?.user?.user?.role?.permissions);

  useEffect(() => {
    if (userPermission) {
      const permissionNavigation = sidebarNavigation
        .filter((item) => {
          return (
            item.children || item.isDefault || getAccessCode(userPermission, item.routeKey) !== PermissionTypes.NOTHING
          );
        })
        .map((item) => {
          if (!item.children) {
            return item;
          }

          const children = item?.children || [];

          const filteredChildren = children?.filter((child) => {
            return getAccessCode(userPermission, child.routeKey) !== PermissionTypes.NOTHING;
          });

          return {
            ...item,
            children: filteredChildren,
          };
        })
        .filter((item) => (item.children ? item.children.length > 0 : item));
      setSideNavigation(permissionNavigation);
    }
  }, [user, userPermission]);

  return (
    <>
      <S.Menu
        mode="inline"
        selectedKeys={defaultSelectedKeys}
        defaultOpenKeys={defaultOpenKeys}
        // Ant Design's onClick gives you the 'key' of the item clicked
        onClick={({ key }) => {
          // Find the item in your flat navigation list to get its URL
          const clickedItem = sidebarNavFlat.find((item) => item.key === key);
          if (clickedItem?.url) {
            navigate(clickedItem.url);
          }
          setCollapsed(true);
        }}
        items={sideNavigation.map((nav) => {
          const isSubMenu = nav.children && nav.children.length > 0;

          return {
            key: nav.key,
            icon: nav.icon,
            // Remove the <Link> from the label
            label: (
              <Badge
                dot={(nav.key === 'chats' && unreadMessage > 0) || (nav.key === 'dealRooms' && unreadDealRoom > 0)}
                offset={[-5, 0]}
              >
                <Typography>{capitalize(t(nav.title))}</Typography>
              </Badge>
            ),
            children: isSubMenu
              ? nav.children?.map((childNav) => ({
                  key: childNav.key,
                  icon: childNav.icon,
                  label: capitalize(t(childNav.title)), // No <Link> here either
                }))
              : null,
          };
        })}
      />
    </>
  );
};

export default SiderMenu;
